import { ColumnType } from "antd/es/table";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import useApiError from "hook/useApiError";
import { useMenu } from "hook/useMenu";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { baseQueryInfo } from "utils/queryUtil";
import { TableColumnsType } from "antd";
import TableButton from "components/atoms/buttons/TableButton";
import { useAWS } from "hook/useAWS";
import ReleaseNotesPrsenter from "./ReleaseNotesPrsenter";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { ReleaseNoteCreateResult } from "api/interfaces/settingsInterface.interface";
import moment from "moment";
import { useAuth } from "components/Auth";
import { isVersionGreater, toTrim } from "utils/functions";
import ToggleComponent from "components/atoms/toggle/Toggle";
import styled from "styled-components";
import { FLEX } from "styles/flex";

export default function ReleaseNotesContainer() {
  const location = useLocation();
  const { user } = useAuth();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const [isAddModal, setIsAddModal] = useState<boolean>(false);

  const [selectedRow, setSelectedRow] = useState<
    ReleaseNoteCreateResult | undefined
  >();

  const { queryNavigate } = useMenu({});
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const { lang, notifySuccess, handleErrorNotify } = useApiError();

  const [queryInfo, setQueryInfo] = useState<BasePagination>({
    ...baseQueryInfo,
    sortType: "version",
  });
  const { docScan, docPut, docDelete } = useAWS();
  const [releaseNotesList, setReleaseNotesList] = useState<any[]>([]);
  const getReleaseNotesList = () => {
    try {
      docScan(process.env.REACT_APP_RELEASE_NOTES_TABLE_NAME as string)
        .then((data: any) => {
          setReleaseNotesList(data.Items);
        })
        .catch((error: any) => {
          console.error(error);
        });
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    getReleaseNotesList();
  }, []);
  const list = useMemo(() => {
    return releaseNotesList
      .filter((item) => {
        if (queryInfo.keyword) {
          return (
            (item.version || "")
              .toLowerCase()
              .includes(queryInfo.keyword.toLowerCase()) ||
            (item.createdEmail || "")
              .toLowerCase()
              .includes(queryInfo.keyword.toLowerCase())
          );
        }
        return true;
      })
      .sort((a, b) => {
        const numnber = queryInfo.sortDirection === SORT_DIRECTION.ASC ? -1 : 1;
        if (queryInfo.sortType === "version") {
          return isVersionGreater({
            installedVersion: a.version,
            latestVersion: b.version,
          })
            ? numnber
            : -numnber;
        }
        return new Date(a.releasedAt).getTime() -
          new Date(b.releasedAt).getTime() >
          0
          ? numnber
          : -numnber;
      });
  }, [queryInfo, releaseNotesList]);
  const { data, status, paginationInfo } = useMemo(() => {
    return {
      data: list.slice(
        queryInfo.pageNum * queryInfo.pageLimit,
        (queryInfo.pageNum + 1) * queryInfo.pageLimit
      ),
      status: {
        totalTitle: "Total",
        totalValue: list.length,
      },
      paginationInfo: {
        ...queryInfo,
        total: list.length,
      },
    };
  }, [list, queryInfo.pageNum, queryInfo.pageLimit]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    setQueryInfo((info) => {
      return {
        ...info,
        pageNum: !search.get("pageNum") ? 0 : Number(search.get("pageNum")),
        sortType: !search.get("sortType")
          ? "version"
          : (search.get("sortType") as string),
        sortDirection: !search.get("sortDirection")
          ? SORT_DIRECTION.DESC
          : (search.get("sortDirection") as SORT_DIRECTION),
        keyword: !search.get("keyword")
          ? ""
          : (search.get("keyword") as string),
      };
    });
  }, [location]);

  const onHeaderCell = (column: ColumnType<any>) => {
    return {
      onClick: () => {
        if (column.key) {
          queryNavigate({
            to: location.pathname,
            queryInfo: {
              ...queryInfo,
              pageNum: 0,
              sortType: column.key as string,
              sortDirection: onSortAntd(queryInfo.sortDirection),
            },
          });
        }
      },
    };
  };
  const columns: TableColumnsType<any> = [
    {
      title: "Version",
      key: "version",
      dataIndex: "version",
      render: (value, row) => value,
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "version"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Release Date",
      key: "releasedAt",
      dataIndex: "releasedAt",
      render: (value, row) =>
        getAccountTimeFormatDate(value, selectedAccount, true, true),
      width: "15%",
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "releasedAt"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Created Date",
      key: "createdAt",
      dataIndex: "createdAt",
      width: "20%",
      render: (value, row) =>
        getAccountTimeFormatDate(value, selectedAccount, true, true),
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "createdAt"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Created By",
      key: "createdBy",
      dataIndex: "createdBy",
      width: "15%",
      render: (value, row) => row.createdEmail,
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "createdBy"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Display",
      key: "isActive",
      dataIndex: "isActive",
      render: (value, row, index) => (
        <ToggleComponent
          isToggled={value}
          checked={value}
          disabled={true}
          hideButton={true}
          dataTestId={`manager-release-notes-table-button-toggle-${index}`}
        />
      ),

      width: 100,
      align: "center",
    },
    // 마지막 버튼
    {
      title: "",
      render: (value, row, index) => (
        <Row className="row-click-button">
          <TableButton
            label="Preview"
            onClick={(e) => {
              e.stopPropagation();
              onModal("preview", row);
            }}
            data-testid={`manager-release-notes-table-button-preview-${index}`}
          />

          <TableButton
            label="Edit"
            onClick={(e) => {
              e.stopPropagation();
              onModal("edit", row);
            }}
            data-testid={`manager-release-notes-table-button-edit-${index}`}
          />
        </Row>
      ),

      align: "right",
      width: 180,
    },
  ];

  const [isPreviewModal, setIsPreviewModal] = useState<boolean>(false);

  const onModal = (type: string, row?: ReleaseNoteCreateResult) => {
    if (type === "preview") {
      setIsPreviewModal(!isPreviewModal);
    } else {
      setIsAddModal(!isAddModal);
    }
    setSelectedRow(row ?? undefined);
  };

  const onChangePage = (page: number, totalRows: number) => {
    queryNavigate({
      to: location.pathname,
      queryInfo: {
        ...queryInfo,
        pageNum: page - 1,
      },
    });
  };
  const onUpdate = (data: ReleaseNoteCreateResult) => {
    setIsUpdateLoading(true);
    let payload = { ...data };
    const isEdit = !!data?.id;
    if (!isEdit) {
      payload.id = releaseNotesList.length + new Date().getTime().toString();
      payload.createdAt = moment().utc().toISOString();
      payload.createdBy = user.name;
      payload.createdEmail = user.email;
      payload.version = toTrim(payload.version);
      payload.content = toTrim(payload.content);
    }
    docPut(process.env.REACT_APP_RELEASE_NOTES_TABLE_NAME as string, payload)
      .then(() => {
        if (isEdit) {
          notifySuccess(lang.label.releaseNotes.update.success);
        } else {
          notifySuccess(lang.label.releaseNotes.add.success);
        }
        getReleaseNotesList();
        setIsAddModal(false);
      })
      .catch((error: any) => {
        console.error(error);
        if (isEdit) {
          handleErrorNotify(error, lang.label.releaseNotes.update.fail);
        } else {
          handleErrorNotify(error, lang.label.releaseNotes.add.fail);
        }
      })
      .finally(() => {
        setIsUpdateLoading(false);
      });
  };
  const onDelete = (id: string) => {
    docDelete(process.env.REACT_APP_RELEASE_NOTES_TABLE_NAME as string, id)
      .then(() => {
        getReleaseNotesList();
        setIsAddModal(false);
      })
      .catch((error: any) => {
        console.error(error);
        handleErrorNotify(error, lang.label.releaseNotes.delete.fail);
      })
      .finally(() => {
        setIsUpdateLoading(false);
      });
  };
  const onClickSearch = (keyword: string) => {
    queryNavigate({
      to: location.pathname,
      queryInfo: {
        ...queryInfo,
        keyword,
        pageNum: 0,
      },
    });
  };

  return (
    <ReleaseNotesPrsenter
      isPreviewModal={isPreviewModal}
      onModal={onModal}
      columns={columns}
      data={data}
      paginationInfo={paginationInfo}
      onChangePage={onChangePage}
      tablePending={false}
      status={status}
      isAddModal={isAddModal}
      selectedRow={selectedRow}
      onUpdate={onUpdate}
      onDelete={onDelete}
      isUpdateLoading={isUpdateLoading}
      onClickSearch={onClickSearch}
    />
  );
}

const Row = styled.div`
  ${FLEX.FlexEndCenter};
  gap: 10px;
`;
